/**
 * One group of the gates panel — the rows blocking Table 12, the rows held out, the rows a person
 * removed, the rows awaiting verification — named by invoice and said in words, a page at a time.
 *
 * It replaced lines like `hsn_missing on row 5335b40c374d — blocks prepared`, 5,891 of them on one
 * real month (2026-09-29). The group opens with its reasons counted, so a month blocked by 3,000
 * missing HSN codes reads as one fact before it reads as 3,000 rows.
 */

import { Button } from '@fiveox/ui'
import { useState } from 'react'

import { GATE_PAGE, type GateRow, reasonCounts, rowNext, rowWhere, rowWhy } from '../lib/gateRows'

export function GateList({
  label,
  consequence,
  rows,
  soft = false,
}: {
  /** "Table 12", "Held out", "Removed by the preparer", "Awaiting verification". */
  label: string
  /** What the group does to the return, e.g. "blocks prepared" — said once, not per row. */
  consequence: string
  rows: GateRow[]
  soft?: boolean
}) {
  const [shown, setShown] = useState(GATE_PAGE)
  if (rows.length === 0) return null
  const counts = reasonCounts(rows)
  const more = Math.min(GATE_PAGE, rows.length - shown)

  return (
    <li className="block">
      <p className="text-sm">
        <span className={`gst-k${soft ? ' is-soft' : ''}`}>{label}</span>{' '}
        <strong className="tabular-nums">{rows.length.toLocaleString('en-IN')}</strong>{' '}
        {rows.length === 1 ? 'row' : 'rows'} — {consequence}
      </p>
      {counts.length > 1 && (
        <p className="gst-meta mt-1">
          {counts.map((c) => `${c.count.toLocaleString('en-IN')} × ${c.why}`).join(' · ')}
        </p>
      )}
      <ul className="mt-2 space-y-1">
        {rows.slice(0, shown).map((row, i) => (
          <li key={String(row.content_key ?? i)} className="text-sm">
            <span>{rowWhere(row)}</span>
            <span className="text-muted-foreground"> — {rowWhy(row)}</span>
            {rowNext(row) && <span> {rowNext(row)}</span>}
          </li>
        ))}
      </ul>
      {more > 0 && (
        <Button type="button" variant="outline" size="sm" className="mt-2"
          onClick={() => setShown((n) => n + GATE_PAGE)}>
          Show {more.toLocaleString('en-IN')} more of {(rows.length - shown).toLocaleString('en-IN')}
        </Button>
      )}
    </li>
  )
}
